import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiArrowDown, FiArrowUp, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiColumns, FiDownload, FiEdit2, FiEye, FiFilter, FiSearch, FiSliders, FiTrash2, FiX } from "react-icons/fi";
import "./ReusableServerDataGrid.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DENSITY_OPTIONS = [
  {
    value: "compact",
    label: "Ngushtë",
    description: "Më shumë rreshta në ekran",
    icon: (
      <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
        <rect x="0" y="0" width="18" height="2" rx="1" fill="currentColor" />
        <rect x="0" y="4" width="18" height="2" rx="1" fill="currentColor" />
        <rect x="0" y="8" width="18" height="2" rx="1" fill="currentColor" />
        <rect x="0" y="12" width="18" height="2" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "comfortable",
    label: "Normale",
    description: "Madhësi standarde",
    icon: (
      <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
        <rect x="0" y="0" width="18" height="3" rx="1" fill="currentColor" />
        <rect x="0" y="5.5" width="18" height="3" rx="1" fill="currentColor" />
        <rect x="0" y="11" width="18" height="3" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "spacious",
    label: "E gjerë",
    description: "Rreshta më të lartë",
    icon: (
      <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
        <rect x="0" y="0" width="18" height="4" rx="1" fill="currentColor" />
        <rect x="0" y="6.5" width="18" height="4" rx="1" fill="currentColor" />
      </svg>
    ),
  },
];

function normalizeQuickFilter(value) {
  if (Array.isArray(value) && value.length > 0) {
    return value.join(" ");
  }
  return "";
}

function normalizeDateFilterValue(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const dotMatch = raw.match(/^(\d{2})[.\/](\d{2})[.\/](\d{4})$/);
  if (dotMatch) {
    const [, day, month, year] = dotMatch;
    return `${year}-${month}-${day}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return raw;
  }

  return null;
}

function formatDateForInput(isoDate) {
  const raw = String(isoDate || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return raw;
  }
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

function addDaysToIsoDate(isoDate, daysToAdd) {
  const base = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(base.getTime())) {
    return null;
  }
  base.setDate(base.getDate() + daysToAdd);
  const year = base.getFullYear();
  const month = String(base.getMonth() + 1).padStart(2, "0");
  const day = String(base.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseNumericFilterInput(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return null;
  }

  const match = raw.match(/^(>=|<=|>|<|=)?\s*(-?\d+(?:[.,]\d+)?)$/);
  if (!match) {
    return null;
  }

  const operatorToken = match[1] || "";
  const numberToken = match[2].replace(",", ".");
  const parsed = Number(numberToken);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  const operatorMap = {
    ">": "gt",
    "<": "lt",
    ">=": "gte",
    "<=": "lte",
    "=": "equals",
    "": "equals",
  };

  const hasDecimal = /[.,]/.test(match[2]);

  return {
    hasExplicitOperator: operatorToken !== "",
    operator: operatorMap[operatorToken] || "equals",
    value: String(parsed),
    numericValue: parsed,
    hasDecimal,
  };
}

const EXPORT_HEADER_TRANSLATIONS = {
  rowNumber: "Nr.",
  InvoiceNr: "Numri i Fatures",
  Date: "Data",
  AccountName: "Klienti",
  AmountWithoutVAT: "Vlera",
  VATTotal: "TVSH",
  Total: "Total",
  PaymentOptions: "Pagesa",
};

function formatExportDate(value) {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();
  const dotMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotMatch) {
    return raw;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}.${month}.${year}`;
  }

  return raw;
}

function getDateStampDdMmYyyy() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  return `${day}.${month}.${year}`;
}

export default function ReusableServerDataGrid({
  columns = [],
  rows = [],
  loading = false,
  rowCount = 0,
  height = 620,
  paginationModel = { page: 0, pageSize: 10 },
  setPaginationModel,
  sortModel = [],
  setSortModel,
  filterModel = { items: [] },
  setFilterModel,
  quickFilter = [],
  setQuickFilter,
  getExportRows,
  requireFullExport = false,
  onExportError,
  exportSummaryFields = [],
  summaryFooter,
  getRowId,
  showPayment = false,
  paymentField = "PaymentOptions",
  paymentLabels = { paid: "Paguar", unpaid: "Pa Paguar" },
  actionsMinWidth,
  actionsFlex,
  showViewAction = false,
  showEditAction = true,
  showDeleteAction = true,
  canEditRow,
  canDeleteRow,
  onView,
  onEdit,
  onDelete,
  handleView,
  handleEdit,
  handleDelete,
}) {
  const resolvedOnView = onView || handleView;
  const resolvedOnEdit = onEdit || handleEdit;
  const resolvedOnDelete = onDelete || handleDelete;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [quickSearch, setQuickSearch] = useState(normalizeQuickFilter(quickFilter));
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDensityMenu, setShowDensityMenu] = useState(false);
  const [density, setDensity] = useState("comfortable");
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [columnWidths, setColumnWidths] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const quickSearchTimerRef = useRef(null);
  const skipFilterModelSyncRef = useRef(false);
  const resizeStateRef = useRef(null);
  const toolbarMenusRef = useRef(null);
  const tableWrapRef = useRef(null);
  const summaryWrapRef = useRef(null);

  const actionColumnNeeded = showViewAction || showEditAction || showDeleteAction;

  useEffect(() => {
    setQuickSearch(normalizeQuickFilter(quickFilter));
  }, [quickFilter]);

  useEffect(() => {
    if (skipFilterModelSyncRef.current) {
      skipFilterModelSyncRef.current = false;
      return;
    }

    const initialFilters = {};
    (filterModel?.items || []).forEach((item) => {
      if (item?.field) {
        if (item.id === `${item.field}__start`) {
          initialFilters[item.field] = formatDateForInput(item.value || "");
          return;
        }

        if (item.id === `${item.field}__min`) {
          initialFilters[item.field] = item.value || "";
          return;
        }

        if (item.field === paymentField) {
          if (item.operator === "gt" && String(item.value) === "0") {
            initialFilters[item.field] = "paid";
            return;
          }
          if (item.operator === "equals" && String(item.value) === "0") {
            initialFilters[item.field] = "unpaid";
            return;
          }
        }

        initialFilters[item.field] = item.value || "";
      }
    });
    setColumnFilters(initialFilters);
  }, [filterModel, paymentField]);

  useEffect(() => {
    if (!setQuickFilter) {
      return undefined;
    }

    if (quickSearchTimerRef.current) {
      clearTimeout(quickSearchTimerRef.current);
    }

    quickSearchTimerRef.current = setTimeout(() => {
      const rawValue = quickSearch;
      const hasSearchText = rawValue.trim().length > 0;
      setQuickFilter(hasSearchText ? [rawValue] : []);
      setPaginationModel?.((prev) => ({ ...(prev || { pageSize: 10 }), page: 0 }));
    }, 300);

    return () => {
      if (quickSearchTimerRef.current) {
        clearTimeout(quickSearchTimerRef.current);
      }
    };
  }, [quickSearch, setQuickFilter, setPaginationModel]);

  const tableColumns = useMemo(() => {
    const nextColumns = [...columns];

    if (showPayment) {
      nextColumns.push({
        field: paymentField,
        headerName: "Pagesa",
        minWidth: 90,
        flex: 0.8,
        sortable: false,
        filterable: true,
        isPaymentColumn: true,
      });
    }

    if (actionColumnNeeded) {
      nextColumns.push({
        field: "actions",
        headerName: "Veprimet",
        minWidth: actionsMinWidth ?? 120,
        flex: actionsFlex ?? 1,
        sortable: false,
        filterable: false,
        isActionColumn: true,
      });
    }

    return nextColumns;
  }, [
    columns,
    showPayment,
    paymentField,
    actionColumnNeeded,
    actionsMinWidth,
    actionsFlex,
  ]);

  const visibleColumns = useMemo(
    () => tableColumns.filter((column) => !hiddenColumns.has(column.field)),
    [tableColumns, hiddenColumns],
  );

  const currentSort = sortModel?.[0] || null;
  const currentPage = paginationModel?.page || 0;
  const currentPageSize = paginationModel?.pageSize || 10;
  const totalPages = Math.max(1, Math.ceil((rowCount || 0) / currentPageSize));

  const resolveRowId = (row, index) => {
    if (getRowId) {
      return getRowId(row);
    }
    return row.id ?? row.TransactionID ?? `row-${index}`;
  };

  const resolvedRows = useMemo(
    () => rows.map((row, index) => ({ row, rowId: resolveRowId(row, index) })),
    [rows, getRowId],
  );

  const visibleRows = resolvedRows;

  const filterableColumns = useMemo(
    () => tableColumns.filter((column) => column.filterable !== false && !column.isActionColumn && !column.isPaymentColumn),
    [tableColumns],
  );

  useEffect(() => {
    const existingColumnFields = new Set(tableColumns.map((column) => column.field));

    setHiddenColumns((prev) => {
      const next = new Set();
      prev.forEach((field) => {
        if (existingColumnFields.has(field)) {
          next.add(field);
        }
      });
      return next;
    });

    setColumnWidths((prev) => {
      const next = {};
      Object.entries(prev).forEach(([field, width]) => {
        if (existingColumnFields.has(field)) {
          next[field] = width;
        }
      });
      return next;
    });
  }, [tableColumns]);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!resizeStateRef.current) {
        return;
      }

      const { field, startX, startWidth, minWidth } = resizeStateRef.current;
      const delta = event.clientX - startX;
      const nextWidth = Math.max(minWidth || 90, startWidth + delta);

      setColumnWidths((prev) => ({
        ...prev,
        [field]: nextWidth,
      }));
    };

    const onMouseUp = () => {
      resizeStateRef.current = null;
      document.body.classList.remove("RSDG__isResizing");
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.classList.remove("RSDG__isResizing");
    };
  }, []);

  useEffect(() => {
    const tableEl = tableWrapRef.current;
    const summaryEl = summaryWrapRef.current;
    if (!tableEl || !summaryEl) {
      return undefined;
    }

    const onScroll = () => {
      summaryEl.scrollLeft = tableEl.scrollLeft;
    };

    tableEl.addEventListener("scroll", onScroll);
    return () => tableEl.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!showColumnsMenu && !showFilterPanel && !showDensityMenu) {
        return;
      }

      if (!toolbarMenusRef.current?.contains(event.target)) {
        setShowColumnsMenu(false);
        setShowDensityMenu(false);
      }
    };

    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [showColumnsMenu, showFilterPanel, showDensityMenu]);

  const updateSort = (field, sortable = true) => {
    if (!sortable || !setSortModel) {
      return;
    }

    let nextSort = [];
    if (!currentSort || currentSort.field !== field) {
      nextSort = [{ field, sort: "asc" }];
    } else if (currentSort.sort === "asc") {
      nextSort = [{ field, sort: "desc" }];
    }

    setSortModel(nextSort);
    setPaginationModel?.((prev) => ({ ...(prev || { pageSize: 10 }), page: 0 }));
  };

  const updateColumnFilter = (field, value) => {
    const nextFilters = { ...columnFilters, [field]: value };
    setColumnFilters(nextFilters);

    if (!setFilterModel) {
      return;
    }

    const dateFields = new Set(["Date", "PayDate", "CreatedDate", "ModifiedDate"]);
    const numericFields = new Set([
      "Total",
      "VATTotal",
      "AmountWithoutVAT",
      "Amount0",
      "Amount8",
      "Amount18",
      "VAT8",
      "VAT18",
      "DiscountAmount",
      "DiscountPercent",
    ]);

    const items = Object.entries(nextFilters)
      .filter(([, filterValue]) => String(filterValue || "").trim() !== "")
      .flatMap(([filterField, filterValue]) => {
        let operator = "contains";
        let normalizedValue = String(filterValue).trim();

        if (filterField === paymentField) {
          const paymentValue = normalizedValue.toLowerCase();
          if (paymentValue === "paid") {
            operator = "gt";
            normalizedValue = "0";
          } else if (paymentValue === "unpaid") {
            operator = "equals";
            normalizedValue = "0";
          } else {
            return null;
          }
        }

        const columnDef = tableColumns.find((col) => col.field === filterField);
        if (columnDef?.filterType === "select") {
          operator = "equals";
        }

        if (dateFields.has(filterField)) {
          const parsedDate = normalizeDateFilterValue(normalizedValue);
          if (!parsedDate) {
            return [];
          }

          const nextDay = addDaysToIsoDate(parsedDate, 1);
          if (!nextDay) {
            return [];
          }

          return [
            {
              id: `${filterField}__start`,
              field: filterField,
              operator: "gte",
              value: parsedDate,
            },
            {
              id: `${filterField}__end`,
              field: filterField,
              operator: "lt",
              value: nextDay,
            },
          ];
        }

        if (numericFields.has(filterField)) {
          const parsedNumeric = parseNumericFilterInput(normalizedValue);
          if (!parsedNumeric) {
            return [];
          }

          if (!parsedNumeric.hasExplicitOperator) {
            const step = parsedNumeric.hasDecimal ? 0.01 : 1;
            const upperBound = parsedNumeric.numericValue + step;
            return [
              {
                id: `${filterField}__min`,
                field: filterField,
                operator: "gte",
                value: String(parsedNumeric.numericValue),
              },
              {
                id: `${filterField}__max`,
                field: filterField,
                operator: "lt",
                value: String(upperBound),
              },
            ];
          }

          operator = parsedNumeric.operator;
          normalizedValue = parsedNumeric.value;
        }

        return [{
          id: filterField,
          field: filterField,
          operator,
          value: normalizedValue,
        }];
      })
      .filter(Boolean);

    skipFilterModelSyncRef.current = true;
    setFilterModel({
      items,
      logicOperator: filterModel?.logicOperator,
      linkOperator: filterModel?.linkOperator,
    });
    setPaginationModel?.((prev) => ({ ...(prev || { pageSize: 10 }), page: 0 }));
  };

  const openDeleteModal = (id) => {
    setDeleteItemId(id);
    setDeleteModalOpen(true);
  };

  const toggleColumnVisibility = (field) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const showAllColumns = () => {
    setHiddenColumns(new Set());
  };

  const hideAllColumns = () => {
    setHiddenColumns(new Set(tableColumns.map((column) => column.field)));
  };

  const toPixelNumber = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (/^\d+(\.\d+)?(px)?$/.test(normalized)) {
        const parsed = Number.parseFloat(normalized);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }

    return null;
  };

  const getColumnWidthValue = (column) => columnWidths[column.field] || column.width || column.minWidth || 120;

  const getColumnCssWidth = (column) => {
    const widthValue = getColumnWidthValue(column);
    if (typeof widthValue === "number") {
      return `${widthValue}px`;
    }

    if (typeof widthValue === "string") {
      return widthValue;
    }

    return "120px";
  };

  const startColumnResize = (event, column) => {
    event.preventDefault();
    event.stopPropagation();

    const baseWidth = getColumnWidthValue(column);
    const parsedPixelWidth = toPixelNumber(baseWidth);

    resizeStateRef.current = {
      field: column.field,
      startX: event.clientX,
      startWidth: parsedPixelWidth || column.minWidth || 120,
      minWidth: column.minWidth || 90,
    };

    document.body.classList.add("RSDG__isResizing");
  };

  const exportVisibleToExcel = async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    try {
    const exportColumns = visibleColumns.filter((column) => !column.isActionColumn);

    let sourceRows = visibleRows.map(({ row }) => row);
    if (typeof getExportRows === "function") {
      try {
        const fetchedRows = await getExportRows();
        if (Array.isArray(fetchedRows)) {
          sourceRows = fetchedRows;
        } else if (requireFullExport) {
          throw new Error("Eksporti nuk ktheu të dhëna valide.");
        }
      } catch (error) {
        if (requireFullExport) throw error;
        // Keep current page fallback for pages that have not enabled strict export.
      }
    }

    const exportRows = sourceRows.map((row) => {
      const output = {};

      exportColumns.forEach((column) => {
        const header = column.headerName || EXPORT_HEADER_TRANSLATIONS[column.field] || column.field;
        let exportValue = "";

        if (column.isPaymentColumn) {
          const paymentValue = row?.[paymentField] ?? row?.rowInfo?.[paymentField] ?? 0;
          exportValue = paymentValue !== 0 ? paymentLabels.paid : paymentLabels.unpaid;
        } else {
          const rawValue = row?.[column.field];

          if (typeof column.exportValueGetter === "function") {
            exportValue = column.exportValueGetter(rawValue, row);
          } else if (typeof column.valueFormatter === "function") {
            try {
              exportValue = column.valueFormatter(rawValue, row);
            } catch (error) {
              exportValue = rawValue;
            }
          } else {
            exportValue = rawValue;
          }

          if (column.field?.toLowerCase().includes("date")) {
            exportValue = formatExportDate(exportValue);
          }
        }

        output[header] = exportValue ?? "";
      });

      return output;
    });

    const xlsx = await import("xlsx");
    const worksheet = xlsx.utils.json_to_sheet(exportRows, {
      header: exportColumns.map(column => column.headerName || EXPORT_HEADER_TRANSLATIONS[column.field] || column.field),
    });
    if (exportSummaryFields.length > 0) {
      const summaryRow = exportRows.length + 1;
      const labelIndex = Math.max(0, exportColumns.findIndex(column => column.field === summaryFooter?.labelField));
      xlsx.utils.sheet_add_aoa(worksheet, [exportColumns.map((column, index) => {
        if (exportSummaryFields.includes(column.field)) return 0;
        return index === labelIndex ? "Totali" : "";
      })], { origin: { r: summaryRow, c: 0 } });
      exportColumns.forEach((column, index) => {
        if (!exportSummaryFields.includes(column.field)) return;
        const address = xlsx.utils.encode_cell({ r: summaryRow, c: index });
        const sum = sourceRows.reduce((total, row) => total + Number(row[column.field] || 0), 0);
        worksheet[address] = { t: "n", v: Number(sum.toFixed(2)), z: "#,##0.00" };
        if (exportRows.length > 0) {
          const letter = xlsx.utils.encode_col(index);
          worksheet[address].f = `SUM(${letter}2:${letter}${summaryRow})`;
        }
      });
    }
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "TeDhena");
    const datePart = getDateStampDdMmYyyy();
    xlsx.writeFile(workbook, `Eksport-${datePart}.xlsx`);
    } catch (error) {
      if (onExportError) onExportError(error);
      else throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setQuickSearch("");
    setFilterModel?.({ items: [] });
    setQuickFilter?.([]);
    setPaginationModel?.((prev) => ({ ...(prev || { pageSize: 10 }), page: 0 }));
  };

  const getCellValue = (column, row, rowIndex) => {
    if (column.isActionColumn) {
      const rowPayload = row.rowInfo || row;
      const rowCanEdit = typeof canEditRow === "function" ? canEditRow(rowPayload, row) : true;
      const rowCanDelete = typeof canDeleteRow === "function" ? canDeleteRow(rowPayload, row) : true;
      return (
        <>
          {showViewAction && (
            <button
              type="button"
              className="RSDG__iconButton RSDG__iconButton--view"
              onClick={(e) => { e.stopPropagation(); resolvedOnView?.(rowPayload); }}
              aria-label="Shfaq"
              title="Shfaq"
            >
              <FiEye size={16} />
            </button>
          )}
          {showEditAction && (
            <button
              type="button"
              className="RSDG__iconButton RSDG__iconButton--edit"
              disabled={!rowCanEdit}
              onClick={(e) => {
                e.stopPropagation();
                if (rowCanEdit) {
                  resolvedOnEdit?.(rowPayload);
                }
              }}
              aria-label="Edito"
              title={rowCanEdit ? "Edito" : "Nuk mund të editohet"}
            >
              <FiEdit2 size={16} />
            </button>
          )}
          {showDeleteAction && (
            <button
              type="button"
              className="RSDG__iconButton RSDG__iconButton--danger"
              disabled={!rowCanDelete}
              onClick={(e) => {
                e.stopPropagation();
                if (rowCanDelete) {
                  openDeleteModal(row.deleteItemId ?? rowPayload.TransactionID ?? resolveRowId(row, rowIndex));
                }
              }}
              aria-label="Fshij"
              title={rowCanDelete ? "Fshij" : "Nuk mund të fshihet"}
            >
              <FiTrash2 size={16} />
            </button>
          )}
        </>
      );
    }

    const rowPayload = row?.rowInfo || row;

    if (column.isPaymentColumn) {
      const paymentValue = rowPayload?.[paymentField] ?? row?.[paymentField] ?? 0;
      const paid = paymentValue !== 0;
      return (
        <span className={`RSDG__badge ${paid ? "RSDG__badge--success" : "RSDG__badge--danger"}`}>
          {paid ? paymentLabels.paid : paymentLabels.unpaid}
        </span>
      );
    }

    const rawValue = row?.[column.field] ?? rowPayload?.[column.field];

    if (typeof column.renderCell === "function") {
      try {
        return column.renderCell({ value: rawValue, row: rowPayload, field: column.field });
      } catch (error) {
        return rawValue;
      }
    }

    if (typeof column.valueFormatter === "function") {
      try {
        return column.valueFormatter(rawValue, rowPayload);
      } catch (error) {
        return rawValue;
      }
    }

    return rawValue ?? "";
  };

  const summaryLayout = useMemo(() => {
    if (!summaryFooter) {
      return null;
    }

    const labelIndex = visibleColumns.findIndex((col) => col.field === summaryFooter.labelField);
    if (labelIndex < 0) {
      return null;
    }

    return {
      labelIndex,
      valueColumns: visibleColumns.slice(labelIndex + 1),
      labelSpan: labelIndex + 1,
    };
  }, [summaryFooter, visibleColumns]);

  const summaryVisibleValues = useMemo(() => {
    if (!summaryFooter) {
      return [];
    }

    const values = summaryFooter.values || {};

    return visibleColumns.map((column) => {
      if (column.field === summaryFooter.labelField) {
        return {
          field: column.field,
          value: `\u03A3 ${summaryFooter.label}`,
          type: "label",
        };
      }

      if (Object.prototype.hasOwnProperty.call(values, column.field)) {
        return {
          field: column.field,
          value: values[column.field],
          type: "value",
        };
      }

      return {
        field: column.field,
        value: "",
        type: "empty",
      };
    });
  }, [summaryFooter, visibleColumns]);

  const columnTrackSizes = useMemo(
    () => visibleColumns.map((column) => getColumnCssWidth(column)),
    [visibleColumns, columnWidths],
  );

  const summaryGridTemplateColumns = useMemo(
    () => columnTrackSizes.join(" "),
    [columnTrackSizes],
  );

  return (
    <div className={`RSDG RSDG--density-${density}${showViewAction && resolvedOnView ? " RSDG--clickable-rows" : ""}${showFilterPanel ? " RSDG--filters-visible" : ""}`} style={{ height }}>
      <div className="RSDG__toolbar">
        <div className="RSDG__toolbarMain">
          <div className="RSDG__toolbarActions" ref={toolbarMenusRef}>
            <button
              type="button"
              className={`RSDG__toolButton ${showFilterPanel ? "RSDG__toolButton--active" : ""}`}
              onClick={() => {
                setShowColumnsMenu(false);
                setShowDensityMenu(false);
                setShowFilterPanel((prev) => !prev);
              }}
            >
              <span className="RSDG__toolButtonContent">
                <FiFilter size={14} />
                <span>Filtrat</span>
                {Object.values(columnFilters).filter((v) => String(v || "").trim() !== "").length > 0 && (
                  <span className="RSDG__filterActiveCount">
                    {Object.values(columnFilters).filter((v) => String(v || "").trim() !== "").length}
                  </span>
                )}
              </span>
            </button>

            <button
              type="button"
              className={`RSDG__toolButton ${showColumnsMenu ? "RSDG__toolButton--active" : ""}`}
              onClick={() => {
                setShowFilterPanel(false);
                setShowDensityMenu(false);
                setShowColumnsMenu((prev) => !prev);
              }}
            >
              <span className="RSDG__toolButtonContent">
                <FiColumns size={14} />
                <span>Kolonat</span>
              </span>
            </button>

            <div className="RSDG__toolMenuWrapper">
              <button
                type="button"
                className={`RSDG__toolButton ${showDensityMenu ? "RSDG__toolButton--active" : ""}`}
                onClick={() => {
                  setShowFilterPanel(false);
                  setShowColumnsMenu(false);
                  setShowDensityMenu((prev) => !prev);
                }}
              >
                <span className="RSDG__toolButtonContent">
                  <FiSliders size={14} />
                  <span>Dendesia</span>
                </span>
              </button>

              {showDensityMenu && (
                <div className="RSDG__densityMenu">
                  <div className="RSDG__densityMenuHeader">Dendësia e rreshtave</div>
                  {DENSITY_OPTIONS.map((option) => {
                    const isActive = density === option.value;
                    return (
                      <button
                        key={`density-${option.value}`}
                        type="button"
                        className={`RSDG__densityOption${isActive ? " RSDG__densityOption--active" : ""}`}
                        onClick={() => {
                          setDensity(option.value);
                          setShowDensityMenu(false);
                        }}
                      >
                        <span className="RSDG__densityOptionIcon">{option.icon}</span>
                        <span className="RSDG__densityOptionText">
                          <span className="RSDG__densityOptionLabel">{option.label}</span>
                          <span className="RSDG__densityOptionDesc">{option.description}</span>
                        </span>
                        {isActive && (
                          <span className="RSDG__densityOptionCheck">
                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                              <path d="M1 5l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              className={`RSDG__toolButton${isExporting ? " RSDG__toolButton--loading" : ""}`}
              onClick={exportVisibleToExcel}
              disabled={isExporting}
            >
              <span className="RSDG__toolButtonContent">
                {isExporting ? <span className="RSDG__buttonSpinner" aria-hidden="true" /> : <FiDownload size={14} />}
                <span>{isExporting ? "Duke eksportuar..." : "Export Excel"}</span>
              </span>
            </button>

            {showColumnsMenu && (
              <div className="RSDG__columnsMenu">
                <div className="RSDG__columnsPanelActions">
                  <button type="button" className="RSDG__toolButton" onClick={showAllColumns}>
                    Show All
                  </button>
                  <button type="button" className="RSDG__toolButton" onClick={hideAllColumns}>
                    Hide All
                  </button>
                </div>

                <div className="RSDG__columnsGrid">
                  {tableColumns.map((column) => {
                    const checked = !hiddenColumns.has(column.field);
                    return (
                      <label key={`column-visibility-${column.field}`} className="RSDG__columnToggle">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleColumnVisibility(column.field)}
                        />
                        <span>{column.headerName}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="RSDG__searchWrap RSDG__searchWrap--toolbar">
              <span className="RSDG__searchIcon" aria-hidden="true">
                <FiSearch size={14} />
              </span>
              <input
                type="text"
                className="RSDG__searchInput"
                placeholder="Kerkim i shpejte..."
                aria-label="Kerkim i shpejte"
                value={quickSearch}
                onChange={(event) => setQuickSearch(event.target.value)}
              />
            </div>

          </div>
        </div>

      </div>

      <div className="RSDG__tableWrap" ref={tableWrapRef}>
        <table className="RSDG__table">
          <colgroup>
            {visibleColumns.map((column, index) => (
              <col key={`col-${column.field}`} style={{ width: columnTrackSizes[index] }} />
            ))}
          </colgroup>
          <thead>
            {showFilterPanel && (
              <tr className="RSDG__filterRow">
                {visibleColumns.map((column, columnIndex) => {
                  const filterable = column.filterable !== false && !column.isActionColumn;
                  const hasValue = String(columnFilters[column.field] || "").trim() !== "";
                  const isFirstFilterCell = columnIndex === 0;
                  return (
                    <th key={`frow-${column.field}`} className="RSDG__filterTh">
                      {filterable ? (
                        column.isPaymentColumn ? (
                          <div className="RSDG__inlineFilterWrap RSDG__inlineFilterWrap--select">
                            {isFirstFilterCell && (
                              <button
                                type="button"
                                className="RSDG__inlineFilterClearAll"
                                onClick={clearAllFilters}
                                title="Pastro të gjitha filtrat"
                              >
                                <FiX size={11} />
                              </button>
                            )}
                            <select
                              className="RSDG__inlineFilterInput RSDG__inlineFilterSelect"
                              value={columnFilters[column.field] || ""}
                              onChange={(e) => updateColumnFilter(column.field, e.target.value)}
                            >
                              <option value="">Të gjitha</option>
                              <option value="paid">{paymentLabels.paid}</option>
                              <option value="unpaid">{paymentLabels.unpaid}</option>
                            </select>
                          </div>
                        ) : column.filterType === "select" ? (
                          <div className="RSDG__inlineFilterWrap RSDG__inlineFilterWrap--select">
                            {isFirstFilterCell && (
                              <button
                                type="button"
                                className="RSDG__inlineFilterClearAll"
                                onClick={clearAllFilters}
                                title="Pastro të gjitha filtrat"
                              >
                                <FiX size={11} />
                              </button>
                            )}
                            <select
                              className="RSDG__inlineFilterInput RSDG__inlineFilterSelect"
                              value={columnFilters[column.field] || ""}
                              onChange={(e) => updateColumnFilter(column.field, e.target.value)}
                            >
                              <option value="">Të gjitha</option>
                              {(column.filterOptions || []).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="RSDG__inlineFilterWrap">
                            {isFirstFilterCell && (
                              <button
                                type="button"
                                className="RSDG__inlineFilterClearAll"
                                onClick={clearAllFilters}
                                title="Pastro të gjitha filtrat"
                              >
                                <FiX size={11} />
                              </button>
                            )}
                            <FiSearch size={11} className="RSDG__inlineFilterIcon" />
                            <input
                              className="RSDG__inlineFilterInput"
                              value={columnFilters[column.field] || ""}
                              onChange={(e) => updateColumnFilter(column.field, e.target.value)}
                              placeholder={column.field === "Date" || column.field === "PayDate" ? "dd.mm.yyyy" : "Filtro..."}
                            />
                            {hasValue && (
                              <button
                                type="button"
                                className="RSDG__inlineFilterClear"
                                onClick={() => updateColumnFilter(column.field, "")}
                                aria-label="Pastro"
                              >
                                <FiX size={10} />
                              </button>
                            )}
                          </div>
                        )
                      ) : (
                        isFirstFilterCell ? (
                          <button
                            type="button"
                            className="RSDG__inlineFilterClearAll"
                            onClick={clearAllFilters}
                            title="Pastro të gjitha filtrat"
                          >
                            <FiX size={11} />
                          </button>
                        ) : null
                      )}
                    </th>
                  );
                })}
              </tr>
            )}

            <tr>
              {visibleColumns.map((column) => {
                const sortable = column.sortable !== false && !column.isActionColumn && !column.isPaymentColumn;
                const isActiveSort = currentSort?.field === column.field;
                const isAsc = isActiveSort && currentSort?.sort === "asc";
                const isDesc = isActiveSort && currentSort?.sort === "desc";

                return (
                  <th
                    key={column.field}
                    className={column.headerClassName === "super-app-theme--header" ? "RSDG__th RSDG__th--main" : "RSDG__th"}
                  >
                    <button
                      type="button"
                      className={`RSDG__thButton ${sortable ? "" : "RSDG__thButton--static"} ${isActiveSort ? "RSDG__thButton--sorted" : ""}`}
                      onClick={() => updateSort(column.field, sortable)}
                    >
                      <span className="RSDG__thLabel">{column.headerName}</span>
                      {sortable && (
                        <span className={`RSDG__sortArrow ${isActiveSort ? "RSDG__sortArrow--active" : "RSDG__sortArrow--idle"}`}>
                          {isAsc ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
                        </span>
                      )}
                    </button>
                    <span
                      className="RSDG__columnResizeHandle"
                      onMouseDown={(event) => startColumnResize(event, column)}
                      role="separator"
                      aria-label={`Resize ${column.headerName}`}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {visibleRows.map(({ row, rowId }, rowIndex) => (
              <tr
                key={rowId}
                className={rowIndex % 2 === 0 ? "RSDG__rowEven" : "RSDG__rowOdd"}
                onClick={() => (showViewAction && resolvedOnView) ? resolvedOnView(row.rowInfo || row) : undefined}
              >
                {visibleColumns.map((column) => (
                  <td key={`${rowId}-${column.field}`} className={`RSDG__td${column.isPaymentColumn ? " RSDG__td--centered" : ""}${column.isActionColumn ? " RSDG__td--actions" : ""}`}>
                    {getCellValue(column, row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}

            {!loading && visibleRows.length === 0 && (
              <tr>
                <td className="RSDG__empty" colSpan={visibleColumns.length}>
                  Nuk ka te dhena.
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      {summaryFooter && summaryLayout && (
        <div ref={summaryWrapRef} className="RSDG__summaryWrap" role="status" aria-live="polite">
          <table className="RSDG__summaryTable">
            <colgroup>
              {visibleColumns.map((column, index) => (
                <col key={`sum-col-${column.field}`} style={{ width: columnTrackSizes[index] }} />
              ))}
            </colgroup>
            <tbody>
              <tr>
                {summaryVisibleValues.map((item) => (
                  <td
                    key={`summary-td-${item.field}`}
                    className={`RSDG__summaryTd RSDG__summaryTd--${item.type}`}
                  >
                    {item.value}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="RSDG__footer">
        <div className="RSDG__footerInner">
          <div className="RSDG__pageSizeWrap">
            <label className="RSDG__pageSizeLabel" htmlFor="rsdg-page-size">Rreshta në faqe:</label>
            <select
              id="rsdg-page-size"
              className="RSDG__pageSize"
              value={currentPageSize}
              onChange={(event) =>
                setPaginationModel?.({
                  page: 0,
                  pageSize: Number(event.target.value),
                })
              }
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <span className="RSDG__footerInfo">
            {rowCount > 0
              ? `${currentPage * currentPageSize + 1}–${Math.min((currentPage + 1) * currentPageSize, rowCount)} nga ${rowCount}`
              : `0 nga ${rowCount}`}
          </span>

          <div className="RSDG__pagerNav">
            <button
              type="button"
              className="RSDG__pagerButton"
              disabled={currentPage <= 0}
              onClick={() => setPaginationModel?.({ page: 0, pageSize: currentPageSize })}
              aria-label="Faqja e parë"
            >
              <FiChevronsLeft size={15} />
            </button>

            <button
              type="button"
              className="RSDG__pagerButton"
              disabled={currentPage <= 0}
              onClick={() => setPaginationModel?.({ page: Math.max(0, currentPage - 1), pageSize: currentPageSize })}
              aria-label="Faqja e mëparshme"
            >
              <FiChevronLeft size={15} />
            </button>

            <button
              type="button"
              className="RSDG__pagerButton"
              disabled={currentPage + 1 >= totalPages}
              onClick={() =>
                setPaginationModel?.({ page: Math.min(totalPages - 1, currentPage + 1), pageSize: currentPageSize })
              }
              aria-label="Faqja tjetër"
            >
              <FiChevronRight size={15} />
            </button>

            <button
              type="button"
              className="RSDG__pagerButton"
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setPaginationModel?.({ page: totalPages - 1, pageSize: currentPageSize })}
              aria-label="Faqja e fundit"
            >
              <FiChevronsRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="RSDG__loadingOverlay">Duke ngarkuar...</div>}

      {deleteModalOpen && (
        <div className="RSDG__modalBackdrop" role="dialog" aria-modal="true">
          <div className="RSDG__modalCard">
            <h3 className="RSDG__modalTitle">Konfirmim</h3>
            <p className="RSDG__modalText">A jeni te sigurt se deshironi ta fshini kete rresht?</p>
            <div className="RSDG__modalActions">
              <button
                type="button"
                className="RSDG__modalButton RSDG__modalButton--danger"
                onClick={async () => {
                  await resolvedOnDelete?.(deleteItemId);
                  setDeleteModalOpen(false);
                }}
              >
                Po
              </button>
              <button
                type="button"
                className="RSDG__modalButton"
                onClick={() => setDeleteModalOpen(false)}
              >
                Jo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
